/**
 * AuthField Component
 * Bespoke label + input + inline error field for the authentication screens
 * (login, forgot-password). Reproduces the auth-specific styling exactly and is
 * intentionally separate from the shared Input/FormField primitives so the login
 * look is decoupled from the rest of the app's forms.
 * @packageDocumentation
 */

import { AlertCircle } from 'lucide-react';
import { forwardRef, type ReactNode, type InputHTMLAttributes } from 'react';

import { cn } from '@/lib/utils';

/**
 * Props for the AuthField component.
 *
 * All standard input attributes (including the spread output of react-hook-form's
 * `register()`) are accepted and forwarded to the underlying `<input>`. The `id`
 * prop is required by the caller to wire `htmlFor`, `aria-invalid`, and
 * `aria-describedby`.
 */
export interface AuthFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Field label text */
  label: string;
  /** Error message; when set, applies the error styling and renders the error paragraph */
  error?: string;
  /** Optional element rendered inside the input wrapper (e.g. a password visibility toggle) */
  endAdornment?: ReactNode;
}

/**
 * Authentication form field: a label, a styled input, and an inline error message.
 *
 * Renders as a fragment (no wrapping element) so the caller's outer element keeps
 * full control of layout and the stagger/fade-in animation — wrap each AuthField
 * in the animated container at the call site. The component forwards its ref to
 * the `<input>` so react-hook-form's `register()` ref attaches correctly.
 *
 * When `endAdornment` is provided, the input is wrapped in a `relative` container
 * and gains right padding (`pr-11`); the adornment is rendered as a sibling, so a
 * caller passing an absolutely-positioned control (e.g. `absolute right-3 top-1/2
 * -translate-y-1/2`) positions over the input as expected.
 *
 * @param { AuthFieldProps } props Component props.
 * @param { React.Ref<HTMLInputElement> } ref Forwarded ref attached to the input.
 * @returns { JSX.Element } The auth field markup.
 */
export const AuthField = forwardRef<HTMLInputElement, AuthFieldProps>(
  ({ id, label, error, className, endAdornment, ...inputProps }, ref) => {
    const input = (
      <input
        {...inputProps}
        id={id}
        ref={ref}
        aria-invalid={error ? 'true' : undefined}
        aria-describedby={error ? `${id}-error` : undefined}
        className={cn(
          'w-full rounded-lg border bg-muted/30 px-4 py-3 text-[15px] text-foreground placeholder:text-muted-foreground transition-all focus:bg-card focus:ring-2 focus:ring-ring/20 focus:border-ring outline-none',
          endAdornment && 'pr-11',
          error
            ? 'border-destructive/40 bg-destructive/5 focus:border-destructive focus:ring-destructive/20'
            : 'border-input',
          className,
        )}
      />
    );

    return (
      <>
        <label
          htmlFor={id}
          className={cn('mb-1.5 block text-sm font-medium', error ? 'text-destructive' : 'text-foreground')}
        >
          {label}
        </label>
        {endAdornment ? (
          <div className="relative">
            {input}
            {endAdornment}
          </div>
        ) : (
          input
        )}
        {error && (
          <p id={`${id}-error`} className="mt-1.5 flex items-center gap-1.5 text-sm text-destructive" role="alert">
            <AlertCircle size={14} />
            {error}
          </p>
        )}
      </>
    );
  },
);
AuthField.displayName = 'AuthField';
